import type { GstRateState } from "@/features/catalog/services/gst-rates-api.service";

/** Who may change GST rates (#514). Reading also admits `admin:catalog:read`. */
export const GST_WRITE_PERMISSION = "admin:catalog:gst";
export const GST_READ_PERMISSIONS = [
	"admin:catalog:read",
	GST_WRITE_PERMISSION,
];

/** 1800 -> "18%", 25 -> "0.25%". */
export function formatRate(rateBp: number | null): string {
	if (rateBp === null) return "—";
	const percent = rateBp / 100;
	return `${Number.isInteger(percent) ? percent : percent.toFixed(2).replace(/0$/, "")}%`;
}

/**
 * A rate's start or end, as the calendar day it is stored for. Rates are dated
 * 00:00 UTC, so the day is read in UTC: in IST the instant is 05:30 the same
 * day, but in a timezone west of UTC it would print as the day before.
 */
export function formatRateDay(iso: string | null): string {
	if (!iso) return "—";
	return new Date(iso).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});
}

/** A `YYYY-MM-DD` sign-off date, read as a calendar day. */
export function formatSignOffDay(day: string | null): string {
	return day ? formatRateDay(`${day}T00:00:00.000Z`) : "—";
}

/** Today as `YYYY-MM-DD` in UTC, the convention rate dates are stored in. */
export function todayUtc(now: Date = new Date()): string {
	return now.toISOString().slice(0, 10);
}

export const STATE_LABEL: Record<GstRateState, string> = {
	in_force: "In force",
	scheduled: "Scheduled",
	closed: "Closed",
	withdrawn: "Withdrawn",
};

export const STATE_VARIANT: Record<
	GstRateState,
	"success" | "info" | "default" | "danger"
> = {
	in_force: "success",
	scheduled: "info",
	closed: "default",
	withdrawn: "danger",
};

/** True when `value` is an http(s) URL, so it may be rendered as a link. */
export function isWebUrl(value: string | null): value is string {
	if (!value) return false;
	try {
		const { protocol } = new URL(value);
		return protocol === "https:" || protocol === "http:";
	} catch {
		return false;
	}
}

/** A rate typed as a percentage: 0 to 40, at most two decimals. */
export function parseRatePercent(text: string): number | null {
	if (!/^\d{1,2}(\.\d{1,2})?$/.test(text.trim())) return null;
	const value = Number(text.trim());
	return value >= 0 && value <= 40 ? value : null;
}

export const PREFIX_PATTERN = /^[0-9]{2,8}$/;
